<template>
  <div class="service-page">
    <ServicePageHeader
      icon="fas fa-briefcase"
      title="Career & Job Placement"
      subtitle="Preparing students for successful careers and meaningful employment"
      show-back-link
    />

    <!-- Main Content -->
    <main class="service-content">
      <div class="container">
        <!-- Overview -->
        <section class="content-section">
          <h2>
            <i class="fas fa-info-circle section-icon yellow" />
            Overview
          </h2>
          <p>
            The Career and Job Placement Office bridges the gap between academic preparation and
            professional success. We connect students with internship opportunities, career resources,
            and potential employers to help launch successful careers.
          </p>
        </section>

        <!-- Services Offered -->
        <section class="content-section">
          <h2>
            <i class="fas fa-list-check section-icon yellow" />
            Services Offered
          </h2>
          <div class="services-grid">
            <div class="service-card">
              <div class="card-icon yellow">
                <i class="fas fa-search" />
              </div>
              <h3>Job Matching</h3>
              <p>Connect with employers looking for fresh graduates and skilled professionals.</p>
            </div>
            <div class="service-card">
              <div class="card-icon green">
                <i class="fas fa-file-alt" />
              </div>
              <h3>Resume Building</h3>
              <p>Professional guidance on creating compelling resumes and cover letters.</p>
            </div>
            <div class="service-card">
              <div class="card-icon yellow">
                <i class="fas fa-comments" />
              </div>
              <h3>Interview Prep</h3>
              <p>Mock interviews and coaching to boost your confidence and skills.</p>
            </div>
            <div class="service-card">
              <div class="card-icon green">
                <i class="fas fa-handshake" />
              </div>
              <h3>Internship Programs</h3>
              <p>Partnerships with companies for hands-on industry experience.</p>
            </div>
            <div class="service-card">
              <div class="card-icon yellow">
                <i class="fas fa-calendar-check" />
              </div>
              <h3>Job Fairs</h3>
              <p>Regular career events connecting students with potential employers.</p>
            </div>
            <div class="service-card">
              <div class="card-icon green">
                <i class="fas fa-chart-line" />
              </div>
              <h3>Career Assessment</h3>
              <p>Aptitude tests and career counseling to find your ideal path.</p>
            </div>
          </div>
        </section>

        <!-- Partner Companies -->
        <section class="content-section">
          <h2>
            <i class="fas fa-building section-icon yellow" />
            Partner Companies
          </h2>
          <p class="mb-1">
            We maintain partnerships with leading companies across various industries:
          </p>
          <div class="partners-grid">
            <div class="partner-badge">
              Technology
            </div>
            <div class="partner-badge">
              Healthcare
            </div>
            <div class="partner-badge">
              Finance
            </div>
            <div class="partner-badge">
              Education
            </div>
            <div class="partner-badge">
              Manufacturing
            </div>
            <div class="partner-badge">
              Government
            </div>
          </div>
        </section>

        <!-- Schedule -->
        <section class="content-section">
          <h2>
            <i class="fas fa-calendar-alt section-icon yellow" />
            Office Hours
          </h2>
          <div class="schedule-card">
            <div class="schedule-row">
              <span class="day">Monday - Friday</span>
              <span class="time yellow">8:00 AM - 5:00 PM</span>
            </div>
            <div class="schedule-row">
              <span class="day">Saturday</span>
              <span class="time yellow">8:00 AM - 12:00 PM</span>
            </div>
            <div class="schedule-row">
              <span class="day">Sunday</span>
              <span class="time yellow">Closed</span>
            </div>
          </div>
        </section>

        <!-- Contact -->
        <section class="content-section">
          <h2>
            <i class="fas fa-phone section-icon yellow" />
            Contact Information
          </h2>
          <div class="contact-info">
            <div class="contact-item">
              <i class="fas fa-map-marker-alt yellow" />
              <span>Room 105, Administration Building</span>
            </div>
            <div class="contact-item">
              <i class="fas fa-phone yellow" />
              <span>(078) 123-4567 Local 105</span>
            </div>
            <div class="contact-item">
              <i class="fas fa-envelope yellow" />
              <span>careers@nec.edu.ph</span>
            </div>
          </div>
        </section>

        <!-- CTA -->
        <section class="cta-section yellow">
          <h2>Ready to Start Your Career?</h2>
          <p>Register with our job placement program and get matched with opportunities.</p>
          <button
            class="cta-button"
            @click="registerJobPlacement"
          >
            <i class="fas fa-user-plus" />
            Register Now
          </button>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import ServicePageHeader from '@/components/landing/ServicePageHeader.vue'
import '@/assets/css/landing/pages/services.css'

export default {
  name: 'CareerService',

  components: {
    ServicePageHeader
  },

  metaInfo: () => ({
    title: 'Career & Job Placement'
  }),

  methods: {
    registerJobPlacement () {
      // TODO: Implement registration
      this.$bvToast.toast('Job placement registration coming soon!', {
        title: 'Coming Soon',
        variant: 'info',
        solid: true
      })
    }
  }
}
</script>

<style scoped>
.service-page {
  min-height: 100vh;
  background: #f8fafc;
}

/* Header */
.service-header {
  position: relative;
  padding: 6rem 1.5rem 4rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  text-align: center;
  overflow: hidden;
}

.service-header.yellow {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.header-bg {
  position: absolute;
  inset: 0;
}

.bg-pattern {
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.header-content {
  position: relative;
  z-index: 1;
  max-width: 700px;
  margin: 0 auto;
}

.back-link {
  position: absolute;
  top: -3rem;
  left: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50px;
  transition: all 0.2s;
}

.back-link:hover {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.header-icon {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 0 auto 1.5rem;
}

.service-header h1 {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 0.75rem;
}

.service-header p {
  font-size: 1.125rem;
  opacity: 0.9;
  margin: 0;
}

/* Content */
.service-content {
  padding: 3rem 0;
}

.container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.content-section {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.content-section h2 {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e3a2f;
  margin: 0 0 1rem;
}

.section-icon {
  color: #10b981;
}

.section-icon.yellow {
  color: #f59e0b;
}

.content-section p {
  color: #64748b;
  line-height: 1.7;
  margin: 0;
}

.mb-1 {
  margin-bottom: 1rem;
}

/* Services Grid */
.services-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 640px) {
  .services-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 900px) {
  .services-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.service-card {
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

.card-icon.green {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.card-icon.yellow {
  background: rgba(251, 191, 36, 0.1);
  color: #f59e0b;
}

.service-card h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #1e3a2f;
  margin: 0 0 0.5rem;
}

.service-card p {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

/* Partners */
.partners-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
}

.partner-badge {
  padding: 0.5rem 1rem;
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Schedule */
.schedule-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 1rem;
}

.schedule-row {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.schedule-row:last-child {
  border-bottom: none;
}

.day {
  font-weight: 500;
  color: #1e3a2f;
}

.time {
  color: #10b981;
  font-weight: 500;
}

.time.yellow {
  color: #f59e0b;
}

/* Contact */
.contact-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #64748b;
}

.contact-item i {
  width: 20px;
  color: #10b981;
}

.contact-item i.yellow {
  color: #f59e0b;
}

/* CTA */
.cta-section {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  color: white;
}

.cta-section.yellow {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.cta-section h2 {
  font-size: 1.5rem;
  margin: 0 0 0.5rem;
}

.cta-section p {
  opacity: 0.9;
  margin: 0 0 1.5rem;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 2rem;
  background: white;
  color: #d97706;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}
</style>
